import React from 'react';
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { Badge } from "@/components/ui/badge";
import { Check, ClipboardCopy, GraduationCap, Lightbulb, Loader2, MessageCircle, Quote, Sparkles } from "lucide-react";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { vocabularyLevelChecker } from '@/lib/vocabularyLevel';
import { getSpeakerLang } from '@/utils/device-detection';
import { UserSettings } from '@/contexts/user-settings-context';
import { useYoudaoTranslation } from '@/hooks/use-youdao-translation';
import { useI18n } from '@/lib/i18n';
import { vocabularyCompatibility } from '@/lib/vocabularyLevel/compatibility-layer';
import { getAllPossibleLemmas } from '@/lib/vocabularyLevel/word-lemmatizer';

export interface WordExplanation {
  translation?: string;
  example?: string;
  usphone?: string;
  ukphone?: string;
  // 🎯 用于 vocabulary hover card 显示
  contextDescription?: string;
  exampleTranslation?: string;
  fullTerm?: string;
  source?: 'vocabulary' | 'youdao' | 'dictionary';
}

interface SaveCardOptions {
  youdaoTranslation?: {
    translation?: string;
    phonetic?: string;
    usphone?: string;
    explains?: string[];
  };
  episodeId?: string;
  episodeTitle?: string;
  contextText?: string;
  episodeThumbnail?: string;
  // 新增：保存时的词汇分类
  status?: 'NEW' | 'FUZZY' | 'MASTERED';
}

type WordHighlightStyle = {
  textClass?: string;
  backgroundClass?: string;
};

interface WordHoverCardProps {
  word: string;
  index: number | string;
  isSearchMatch?: boolean;
  isWordCheckerReady?: boolean;
  userSettings?: UserSettings;
  explanations: Record<string, WordExplanation>;
  loading: Record<string, boolean>;
  savedWords: Record<string, boolean>;
  savingWord: Record<string, boolean>;
  onGetExplanation: (word: string) => void;
  onSaveCard: (word: string, options?: SaveCardOptions) => void;
  isCardSaved: (word: string) => boolean;
  selectedWord: string;
  showWordDrawer: boolean;
  setSelectedWord: (word: string) => void;
  setShowWordDrawer: (show: boolean) => void;
  speakerRef?: React.RefObject<HTMLDivElement>;
  // 添加有道翻译支持
  youdaoTranslations?: Record<string, any>;
  youdaoLoading?: Record<string, boolean>;
  youdaoGetTranslation?: (word: string) => void;
  youdaoIsInitialized?: boolean;
  // 保存上下文所需的可选信息
  episodeId?: string;
  episodeTitle?: string;
  contextText?: string;
  episodeThumbnail?: string;
  variant?: 'default' | 'subtitle';
  isHighlighted?: boolean;
  enableMobileDrawer?: boolean;
  highlightStyle?: WordHighlightStyle;
}

const STATUS_DEFINITIONS = [
  {
    value: 'NEW',
    labelKey: 'vocabulary.status.new',
    descriptionKey: 'vocabulary.status.newDescription',
    fallbackLabel: 'New',
    fallbackDescription: 'Just added, needs first review',
  },
  {
    value: 'FUZZY',
    labelKey: 'vocabulary.status.fuzzy',
    descriptionKey: 'vocabulary.status.fuzzyDescription',
    fallbackLabel: 'Fuzzy',
    fallbackDescription: 'Needs more practice soon',
  },
  {
    value: 'MASTERED',
    labelKey: 'vocabulary.status.mastered',
    descriptionKey: 'vocabulary.status.masteredDescription',
    fallbackLabel: 'Mastered',
    fallbackDescription: 'Confident and on long review',
  },
] as const;

type StatusValue = typeof STATUS_DEFINITIONS[number]['value'];

interface StatusOption {
  value: StatusValue;
  label: string;
  description: string;
}

const statusStyles: Record<StatusValue, string> = {
  NEW: 'bg-sky-100 text-sky-700 border-sky-200',
  FUZZY: 'bg-amber-100 text-amber-700 border-amber-200',
  MASTERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const statusAccentMap: Record<StatusValue, string> = {
  NEW: 'bg-sky-400',
  FUZZY: 'bg-amber-400',
  MASTERED: 'bg-emerald-400',
};

const WORD_HIGHLIGHT_BASE_CLASS =
  'font-[13px] leading-tight rounded-[6px] px-[2px] py-[0.5px] shadow-sm ring-1 ring-black/5 dark:ring-white/10';

const WORD_HIGHLIGHT_DEFAULT_CLASS =
  'bg-[#d97757] text-white dark:bg-[#be6b55] dark:text-white';

const WORD_HIGHLIGHT_COLOR_CLASSES = [
  'bg-[#f0f3e8] text-slate-900 dark:bg-[#1f3d30] dark:text-emerald-100',
  'bg-[#fcd34d] text-slate-900 dark:bg-[#7c5200] dark:text-amber-100',
  'bg-[#f472b6] text-white dark:bg-[#9d174d] dark:text-pink-100',
  'bg-[#8b5cf6] text-white dark:bg-[#4c1d95] dark:text-indigo-100',
  'bg-[#10b981] text-white dark:bg-[#065f46] dark:text-emerald-100',
  'bg-[#eab308] text-slate-900 dark:bg-[#854d0e] dark:text-amber-50',
  'bg-[#1e293b] text-slate-100 dark:bg-[#0f172a] dark:text-slate-100',
  'bg-[#0f172a] text-slate-100 dark:bg-[#020617] dark:text-slate-100',
];

const addImportantToClasses = (classNames?: string) => {
  if (!classNames) return classNames;

  return classNames
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => {
      if (token.includes('!')) {
        return token;
      }

      const segments = token.split(':');
      const base = segments.pop() ?? token;
      segments.push(`!${base}`);
      return segments.join(':');
    })
    .join(' ');
};
export function WordHoverCard({
  word,
  index,
  isSearchMatch = false,
  isWordCheckerReady = false,
  userSettings,
  explanations,
  loading,
  savingWord,
  onGetExplanation,
  onSaveCard,
  isCardSaved,
  selectedWord,
  showWordDrawer,
  setSelectedWord,
  setShowWordDrawer,
  speakerRef,
  // 有道翻译参数
  youdaoTranslations,
  youdaoLoading,
  youdaoGetTranslation,
  youdaoIsInitialized,
  // 保存上下文
  episodeId,
  episodeTitle,
  contextText,
  episodeThumbnail,
  variant = 'default',
  isHighlighted = false,
  enableMobileDrawer = true,
  highlightStyle,
}: WordHoverCardProps) {
  const isMobile = useIsMobile();
  const desktopSpeakerRef = React.useRef<HTMLDivElement>(null);
  const { t } = useI18n();

  const statusOptions = React.useMemo<StatusOption[]>(() =>
    STATUS_DEFINITIONS.map((definition) => ({
      value: definition.value,
      label: t(definition.labelKey, definition.fallbackLabel),
      description: t(definition.descriptionKey, definition.fallbackDescription),
    })),
  [t]);

  // 词汇分类状态（默认 NEW）
  const [statusPopoverOpen, setStatusPopoverOpen] = React.useState(false);
  const [selectedStatusValue, setSelectedStatusValue] = React.useState<StatusValue>('NEW');
  const selectedStatus = React.useMemo<StatusOption>(() => {
    const fallback: StatusOption = {
      value: 'NEW',
      label: t('vocabulary.status.new', 'New'),
      description: t('vocabulary.status.newDescription', 'Just added, needs first review'),
    };
    return statusOptions.find((option) => option.value === selectedStatusValue) ?? fallback;
  }, [selectedStatusValue, statusOptions, t]);
  // 控制 HoverCard 开关，防止内部 Popover 操作时被关闭
  const [hoverOpen, setHoverOpen] = React.useState(false);
  // 选择后短暂锁定 HoverCard，避免交互抖动
  const hoverLockRef = React.useRef<number>(0);
  const LOCK_MS = 1500;
  
  // 在Word Highlight模式下使用内部hook，在chunk模式下使用传入的参数
  const internalYoudao = useYoudaoTranslation(isSearchMatch); // 只在Word Highlight模式下初始化
  
  // 根据模式选择使用内部hook还是传入的参数
  const translations = isSearchMatch ? internalYoudao.translations : (youdaoTranslations || {});
  const youdaoLoadingState = isSearchMatch ? internalYoudao.loading : (youdaoLoading || {});
  const getTranslation = isSearchMatch ? internalYoudao.getTranslation : youdaoGetTranslation;
  const isInitialized = isSearchMatch ? internalYoudao.isInitialized : (youdaoIsInitialized || false);
  const resolveLemmaSync = React.useCallback((): { value: string | null; source: 'dictionary' | 'heuristic' | null } => {
    const lowerWord = word.toLowerCase();
    try {
      const base = vocabularyLevelChecker?.getLemma?.(word);
      if (base && base.toLowerCase() !== lowerWord) {
        return { value: base, source: 'dictionary' };
      }
    } catch (error) {
      console.warn(`Failed to resolve lemma for "${word}" via dictionary:`, error);
    }

    try {
      const candidates = getAllPossibleLemmas(lowerWord) || [];
      const heuristic = candidates.find(candidate => candidate && candidate !== lowerWord);
      if (heuristic) {
        return { value: heuristic, source: 'heuristic' };
      }
    } catch (error) {
      console.warn(`Failed to resolve lemma heuristically for "${word}":`, error);
    }

    return { value: null, source: null };
  }, [word]);

  const [lemmaInfo, setLemmaInfo] = React.useState<{ value: string | null; source: 'dictionary' | 'heuristic' | null }>(() => resolveLemmaSync());

  React.useEffect(() => {
    let cancelled = false;
    const syncInfo = resolveLemmaSync();
    setLemmaInfo(syncInfo);

    const needsDictionaryRefresh = syncInfo.source !== 'dictionary';
    if (needsDictionaryRefresh && vocabularyLevelChecker?.hasTranslationAsync) {
      (async () => {
        try {
          await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
          const refreshed = resolveLemmaSync();
          if (!cancelled) {
            setLemmaInfo(refreshed);
          }
        } catch (error) {
          if (!cancelled && syncInfo.value) {
            // Keep heuristic result if we already had one, otherwise clear
            setLemmaInfo(syncInfo);
          }
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [resolveLemmaSync, word]);
  const StatusIconComponent = selectedStatusValue === 'MASTERED'
    ? Check
    : selectedStatusValue === 'FUZZY'
      ? Lightbulb
      : Sparkles;

  const highlightColorClass = React.useMemo(() => {
    if (!isSearchMatch) {
      return WORD_HIGHLIGHT_DEFAULT_CLASS;
    }

    if (!word) {
      return WORD_HIGHLIGHT_COLOR_CLASSES[0] ?? WORD_HIGHLIGHT_DEFAULT_CLASS;
    }

    const palette = WORD_HIGHLIGHT_COLOR_CLASSES;
    if (palette.length === 0) {
      return WORD_HIGHLIGHT_DEFAULT_CLASS;
    }

    const normalized = word.toLowerCase();
    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
      hash = (hash << 5) - hash + normalized.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }

    const paletteIndex = Math.abs(hash) % palette.length;
    return palette[paletteIndex];
  }, [isSearchMatch, word]);
  
  // 当下拉展开时，强制保持 HoverCard 打开
  React.useEffect(() => {
    if (statusPopoverOpen) {
      setHoverOpen(true);
    }
  }, [statusPopoverOpen]);

  const handleSaveWord = React.useCallback(() => {
    if (isCardSaved(word) || savingWord[word]) {
      return;
    }

    const explanation = explanations[word];
    const youdaoData = translations[word];

    if (!explanation && !youdaoData) {
      return;
    }

    const options: SaveCardOptions = {
      episodeId,
      episodeTitle,
      contextText,
      episodeThumbnail,
      status: selectedStatusValue
    };

    if (!explanation && youdaoData) {
      options.youdaoTranslation = {
        translation: youdaoData.translation,
        phonetic: youdaoData.phonetic,
        usphone: youdaoData.usphone,
        explains: youdaoData.explains,
      };
    }

    onSaveCard(word, options);
  }, [
    episodeId,
    episodeThumbnail,
    episodeTitle,
    contextText,
    explanations,
    isCardSaved,
    onSaveCard,
    savingWord,
    selectedStatusValue,
    translations,
    word,
  ]);


  // Check if word should have hover card
  const shouldRenderHoverCard = () => {
    // In Word Highlight mode (isSearchMatch = true), only show vocabulary words
    if (isSearchMatch) {
      // Check if this word is in the vocabulary (has an explanation or is marked for highlighting)
      // This is the original logic for Word Highlight mode
      if (!isWordCheckerReady) return false;
      
      // Check for contractions
      const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
      if (isContraction) return false;

      // Check if it's a valid English word
      const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
      if (!isValidWord) return false;
      
      // For Word Highlight mode, check if word is in vocabulary using hasTranslation
      return vocabularyLevelChecker.hasTranslation(word.toLowerCase());
    }
    
    // For chunk highlight mode (when isSearchMatch is false), 
    // we want to show all valid English words with Youdao translation
    
    // Check for contractions
    const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
    if (isContraction) return false;

    // Check if it's a valid English word
    const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
    return isValidWord; // Return true for all valid words in chunk highlight mode
  };

  if (!shouldRenderHoverCard()) {
    return <span key={index}>{word}</span>;
  }

  const subtitleBaseClass =
    'leading-tight whitespace-normal break-words align-baseline transition-colors duration-150';
  const highlightBackgroundClass = highlightStyle?.backgroundClass;
  const importantBackgroundClass = addImportantToClasses(highlightBackgroundClass);
  const highlightTextClass = highlightStyle?.textClass;
  const importantTextClass = addImportantToClasses(highlightTextClass);
  const isTransparentBackground =
    highlightBackgroundClass
      ?.split(/\s+/)
      .every((cls) => cls.includes('bg-transparent')) ?? false;
  const subtitleHighlightedClass = isTransparentBackground
    ? 'inline font-oldschool-grotesk font-semibold'
    : 'inline-block rounded-md px-1 py-[2.5px] leading-none align-baseline font-oldschool-grotesk font-semibold';
  const subtitleStateClass = isHighlighted
    ? cn(
        subtitleHighlightedClass,
        importantBackgroundClass,
        !highlightBackgroundClass && 'bg-[#d97757]',
        importantTextClass,
        !highlightTextClass && 'text-white',
      )
    : 'inline bg-transparent text-current';

  const triggerClassName = cn(
    'group group/word relative cursor-pointer transition-colors duration-150',
    variant === 'default' && 'inline-block',
    variant === 'default' && WORD_HIGHLIGHT_BASE_CLASS,
    variant === 'default' && highlightColorClass,
    variant === 'subtitle' && subtitleBaseClass,
    variant === 'subtitle' && subtitleStateClass,
  );

  const mobileWordSpan = (
    <span
      key={index}
      className={triggerClassName}
      data-word-highlight-element
      onTouchStart={(e) => {
            e.stopPropagation();
            setSelectedWord(word);
            if (isSearchMatch) {
              onGetExplanation(word);
            } else {
              // Chunk模式：优先从数据库获取，如果没有再使用有道翻译
              // 使用异步方法等待数据库查询完成
              const checkWordInDatabase = async () => {
                try {
                  const currentSourceFile = vocabularyCompatibility.getCurrentSourceFile();
                  
                  // 临时清除词库过滤，以便搜索所有词库
                  const originalSourceFile = currentSourceFile;
                  if (currentSourceFile) {
                    vocabularyCompatibility.setCurrentSourceFile(null);
                  }
                  
                  const hasInDB = await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
                  
                  // 恢复原来的词库过滤设置
                  if (originalSourceFile) {
                    vocabularyCompatibility.setCurrentSourceFile(originalSourceFile);
                  }
                  
                  if (hasInDB && !explanations[word]) {
                    // 如果数据库有但还没加载，从数据库获取
                    onGetExplanation(word);
                  } else if (!hasInDB && isInitialized && getTranslation) {
                    // 如果数据库没有，使用有道翻译
                    getTranslation(word);
                  }
                } catch (error) {
                  console.error(`❌ Mobile touchstart检查单词 "${word}" 时出错:`, error);
                  // 出错时回退到有道翻译
                  if (isInitialized && getTranslation) {
                    getTranslation(word);
                  }
                }
              };
              checkWordInDatabase();
            }
            setShowWordDrawer(true);
          }}
      onClick={(e) => {
            // 在移动设备上，优先使用touchstart事件，避免重复触发
            if (isMobile) {
              // 如果drawer已经显示，说明touchstart已经处理了，忽略click事件
              if (showWordDrawer) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              // 延迟处理以避免与touchstart冲突
              setTimeout(() => {
                if (!showWordDrawer) {
                  setSelectedWord(word);
                  if (isSearchMatch) {
                    onGetExplanation(word);
                  } else {
                    // Chunk模式：优先从数据库获取，如果没有再使用有道翻译
                    const checkWordInDatabase = async () => {
                      try {
                        const currentSourceFile = vocabularyCompatibility.getCurrentSourceFile();
                        
                        // 临时清除词库过滤，以便搜索所有词库
                        const originalSourceFile = currentSourceFile;
                        if (currentSourceFile) {
                          vocabularyCompatibility.setCurrentSourceFile(null);
                        }
                        
                        const hasInDB = await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
                        
                        // 恢复原来的词库过滤设置
                        if (originalSourceFile) {
                          vocabularyCompatibility.setCurrentSourceFile(originalSourceFile);
                        }
                        
                        if (hasInDB && !explanations[word]) {
                          // 如果数据库有但还没加载，从数据库获取
                          onGetExplanation(word);
                        } else if (!hasInDB && isInitialized && getTranslation) {
                          // 如果数据库没有，使用有道翻译
                          getTranslation(word);
                        }
                      } catch (error) {
                        console.error(`❌ Mobile click延迟检查单词 "${word}" 时出错:`, error);
                        // 出错时回退到有道翻译
                        if (isInitialized && getTranslation) {
                          getTranslation(word);
                        }
                      }
                    };
                    checkWordInDatabase();
                  }
                  setShowWordDrawer(true);
                }
              }, 100);
              return;
            }
            
            // 桌面设备使用click事件
            e.preventDefault();
            e.stopPropagation();
            setSelectedWord(word);
            if (isSearchMatch) {
              onGetExplanation(word);
            } else {
              // Chunk模式：优先从数据库获取，如果没有再使用有道翻译
              const checkWordInDatabase = async () => {
                try {
                  const currentSourceFile = vocabularyCompatibility.getCurrentSourceFile();
                  
                  // 临时清除词库过滤，以便搜索所有词库
                  const originalSourceFile = currentSourceFile;
                  if (currentSourceFile) {
                    vocabularyCompatibility.setCurrentSourceFile(null);
                  }
                  
                  const hasInDB = await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
                  
                  // 恢复原来的词库过滤设置
                  if (originalSourceFile) {
                    vocabularyCompatibility.setCurrentSourceFile(originalSourceFile);
                  }
                  
                  if (hasInDB && !explanations[word]) {
                    // 如果数据库有但还没加载，从数据库获取
                    onGetExplanation(word);
                  } else if (!hasInDB && isInitialized && getTranslation) {
                    // 如果数据库没有，使用有道翻译
                    getTranslation(word);
                  }
                } catch (error) {
                  console.error(`❌ Desktop click检查单词 "${word}" 时出错:`, error);
                  // 出错时回退到有道翻译
                  if (isInitialized && getTranslation) {
                    getTranslation(word);
                  }
                }
              };
              checkWordInDatabase();
            }
            setShowWordDrawer(true);
          }}
        >
          {word}
          {!isSearchMatch && variant !== 'subtitle' && (
            <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover/word:opacity-100 transition-opacity duration-200">
              <Lightbulb className="h-3 w-3 text-primary" />
            </span>
          )}
        </span>
  );

  // Mobile version with optional internal drawer
  if (isMobile) {
    if (!enableMobileDrawer) {
      return mobileWordSpan;
    }

    return (
      <>
        {mobileWordSpan}
        {isSearchMatch && selectedWord === word && (
          <Drawer open={showWordDrawer} onOpenChange={setShowWordDrawer}>
            <DrawerContent className='rounded-t-3xl border border-border'>
              <DrawerHeader>
                <DrawerTitle className="flex items-center space-x-2">
                  <GraduationCap className="h-6 w-6 text-primary" />
                  {/* 🎯 显示完整的 vocabulary term（如果存在） */}
                  <span className="text-2xl font-bold text-foreground">{explanations[selectedWord]?.fullTerm || selectedWord}</span>
                </DrawerTitle>
                {(explanations[selectedWord]?.usphone || translations[selectedWord]?.usphone || translations[selectedWord]?.phonetic) && (
                  <DrawerDescription className="text-base text-start pl-8 font-medium text-muted-foreground">
                    /{explanations[selectedWord]?.usphone || translations[selectedWord]?.usphone || translations[selectedWord]?.phonetic}/
                  </DrawerDescription>
                )}
              </DrawerHeader>
              
              <div className="px-4 pb-4">
                {(loading[selectedWord] || youdaoLoadingState[selectedWord]) ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
                  </div>
                ) : (explanations[selectedWord] || translations[selectedWord]) ? (
                  <div className="space-y-4">
                    {/* 翻译内容 - 优先数据库，其次有道 */}
                    {(explanations[selectedWord]?.translation || translations[selectedWord]?.translation || translations[selectedWord]?.explains?.join('; ')) && (
                      <div className="flex items-start space-x-3">
                        <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div className="text-md font-medium text-muted-foreground">
                          {explanations[selectedWord]?.translation ? (
                            typeof explanations[selectedWord]?.translation === 'string' ? (
                              explanations[selectedWord]?.translation?.split(';').map((part: string, index: number) => (
                                <div key={index} className={index > 0 ? "mt-2" : ""}>
                                  {part.trim()}
                                </div>
                              ))
                            ) : (
                              <div>{explanations[selectedWord]?.translation}</div>
                            )
                          ) : (
                            <div>{translations[selectedWord]?.translation || translations[selectedWord]?.explains?.join('; ')}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {lemmaInfo.value && (
                      <div className="flex items-start space-x-3 text-xs uppercase tracking-wide text-muted-foreground">
                        <Sparkles className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          {lemmaInfo.source === 'heuristic'
                            ? t('vocabulary.baseFormSuggestedLabel', 'Base form (suggested)')
                            : t('vocabulary.baseFormLabel', 'Base form')}
                          {": "}
                          <span className="font-semibold normal-case text-foreground">{lemmaInfo.value}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* 详细解释（有道翻译，当没有数据库例句时显示） */}
                    {!explanations[selectedWord]?.example && translations[selectedWord]?.explains && translations[selectedWord]?.explains?.length > 1 && (
                      <div className="flex items-start space-x-3 pt-3 border-t border-border">
                        <Quote className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="text-sm text-muted-foreground">
                          {translations[selectedWord]?.explains?.slice(1).map((explain: string, index: number) => (
                            <div key={index} className={index > 0 ? "mt-1" : ""}>
                              {explain}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 数据库例句 */}
                    {explanations[selectedWord]?.example && (
                      <div className="flex items-start space-x-3 pt-3 border-t border-border">
                        <Quote className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                        <span className="text-base text-muted-foreground italic">
                          {explanations[selectedWord]?.example}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
                    <span className="text-sm">{t('common.loading')}</span>
                  </div>
                )}
              </div>
              
              <DrawerFooter>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="lg"
                    className="flex-1 gap-2"
                    // disabled={
                    //   !userSettings?.myVocabularySetting ||
                    //   savingWord[word] ||
                    //   (!explanations[word] && !translations[word])
                    // }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSaveWord();
                    }}
                  >
                    {savingWord[word] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isCardSaved(word) ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <ClipboardCopy className="h-4 w-4" />
                    )}
                    <span>
                      {isCardSaved(word)
                        ? t('common.saved', 'Saved')
                        : t('common.save')}
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="flex-1 rounded-2xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (speakerRef?.current) {
                        speakerRef.current.click();
                      }
                    }}
                  >
                    <SpeakerIcon
                      divRef={speakerRef}
                      size={10}
                      provider="EdgeTTS"
                      text={selectedWord}
                      lang={getSpeakerLang(userSettings?.languageSetting || "english") as any}
                      voice={undefined}
                      rate={10}
                      volume={100}
                    />
                    <span className="ml-2">{t('common.speak')}</span>
                  </Button>
                </div>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        )}
      </>
    );
  }

  // Desktop version with HoverCard
  return (
    <HoverCard
      key={index}
      open={hoverOpen}
      closeDelay={200}
      onOpenChange={(open) => {
        // 当下拉弹层打开或在短暂锁定期内时，忽略关闭事件以保持 HoverCard 可见
    if (!open && (statusPopoverOpen || Date.now() < hoverLockRef.current)) {
      return;
    }

      if (open) {
        // Keep the hover card open briefly during initial data fetching to avoid flicker
        hoverLockRef.current = Date.now() + LOCK_MS;
        if (isSearchMatch) {
          // Word Highlight模式使用原有的解释服务
          onGetExplanation(word);
        } else {
            // Chunk模式：优先从数据库获取，如果没有再使用有道翻译
            // 使用异步方法等待数据库查询完成
            const checkWordInDatabase = async () => {
              try {
                // 对于chunk模式，我们应该搜索所有词库，而不是限制在特定词库中
                const currentSourceFile = vocabularyCompatibility.getCurrentSourceFile();
                
                // 临时清除词库过滤，以便搜索所有词库
                const originalSourceFile = currentSourceFile;
                if (currentSourceFile) {
                  vocabularyCompatibility.setCurrentSourceFile(null);
                }
                
                // 使用异步方法检查数据库是否有这个词（搜索所有词库）
                const hasInDB = await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
                
                // 恢复原来的词库过滤设置
                if (originalSourceFile) {
                  vocabularyCompatibility.setCurrentSourceFile(originalSourceFile);
                }
                
                if (hasInDB && !explanations[word]) {
                  // 如果数据库有但还没加载，从数据库获取
                  onGetExplanation(word);
                } else if (!hasInDB && isInitialized && getTranslation && !translations[word]) {
                  // 如果数据库没有，使用有道翻译
                  getTranslation(word);
                }
              } catch (error) {
                console.error(`❌ 检查单词 "${word}" 时出错:`, error);
                // 出错时回退到有道翻译
                if (isInitialized && getTranslation && !translations[word]) {
                  getTranslation(word);
                }
              }
            };
            
            // 执行异步检查
            checkWordInDatabase();
          }
        }
        setHoverOpen(open);
      }}
    >
      <HoverCardTrigger asChild>
        <span className={triggerClassName} data-word-highlight-element>
          {word}
          {!isSearchMatch && variant !== 'subtitle' && (
            <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover/word:opacity-100 transition-opacity duration-200">
              <Lightbulb className="h-3 w-3 text-primary" />
            </span>
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side={isSearchMatch ? undefined : "top"}
        sideOffset={isSearchMatch ? 4 : 8}
        className="min-w-[420px] max-w-[520px] rounded-3xl border border-border/70 bg-popover/95 p-4 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-popover/75"
        onPointerEnter={() => setHoverOpen(true)}
        onPointerLeave={() => {
          // 仅当未打开下拉且不在锁定期才允许关闭
          if (!statusPopoverOpen && Date.now() >= hoverLockRef.current) {
            setHoverOpen(false);
          }
        }}
        onPointerDownOutside={(e: any) => {
          if (statusPopoverOpen || Date.now() < hoverLockRef.current) {
            e.preventDefault();
          }
        }}
        onFocusOutside={(e: any) => {
          if (statusPopoverOpen || Date.now() < hoverLockRef.current) {
            e.preventDefault();
          }
        }}
        // 防止选择下拉项时触发外部交互导致关闭
        onInteractOutside={(e: any) => {
          if (statusPopoverOpen || Date.now() < hoverLockRef.current) {
            e.preventDefault();
          }
        }}
      >
        <div className="space-y-4">
          {/* Header Section */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary stroke-[1.5] flex-shrink-0" />
                <h4 className="text-xl font-semibold text-foreground whitespace-nowrap">
                  {/* 🎯 显示完整的 vocabulary term（如果存在） */}
                  {explanations[word]?.fullTerm || word}
                </h4>
              </div>
              {(explanations[word]?.usphone || translations[word]?.usphone || translations[word]?.phonetic) && (
                <div className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
                  <div className="h-1 w-1 rounded-full bg-muted-foreground/60" />
                  <span>
                    /{explanations[word]?.usphone || translations[word]?.usphone || translations[word]?.phonetic}/
                  </span>
                </div>
              )}
              <Badge
                variant="outline"
                className={cn(
                  'mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow-sm',
                  statusStyles[selectedStatusValue]
                )}
              >
                <StatusIconComponent className="h-3 w-3" />
                {selectedStatus.label}
              </Badge>
            </div>
            {(loading[word] || youdaoLoadingState[word]) ? (
              <div className="h-8 w-8 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {/* 词汇分类选择 */}
                <Popover
                  open={statusPopoverOpen}
                  onOpenChange={(open) => {
                    setStatusPopoverOpen(open);
                    // 打开或刚关闭时，保持 HoverCard 短暂开启，避免抖动
                    setHoverOpen(true);
                    hoverLockRef.current = Date.now() + LOCK_MS;
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'h-8 min-w-[90px] justify-between rounded-lg border border-border bg-background/70 text-xs font-medium transition-colors hover:border-primary/50'
                      )}
                      aria-label={t('vocabulary.status.label', 'Vocabulary classification')}
                      title={t('vocabulary.status.label', 'Vocabulary classification')}
                      onPointerDown={(e) => {
                        // 阻止事件冒泡到 HoverCard，但保留 Popover 自身的行为
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        // 让 Radix 处理打开/关闭，不阻断自身事件
                        e.stopPropagation();
                      }}
                    >
                      <span className="flex items-center gap-1 text-foreground">
                        <StatusIconComponent className="h-3 w-3" />
                        {selectedStatus.label}
                      </span>
                      <span className={cn('h-2 w-2 rounded-full', statusAccentMap[selectedStatusValue])} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="z-[100] w-[220px] p-0 rounded-2xl"
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e: any) => e.preventDefault()}
                    onPointerDown={(e) => {
                      // 在弹层内部点击不影响 HoverCard
                      e.stopPropagation();
                    }}
                    onMouseDown={() => {
                      // 在任意点击弹层内容时加锁，优先于外层关闭逻辑
                      hoverLockRef.current = Date.now() + LOCK_MS;
                      setHoverOpen(true);
                    }}
                    onPointerEnter={() => setHoverOpen(true)}
                    onPointerLeave={() => {
                      // 离开弹层时，若未进入内容区，则依据锁定状态决定是否关闭
                      if (!statusPopoverOpen && Date.now() >= hoverLockRef.current) {
                        setHoverOpen(false);
                      }
                    }}
                  >
                    <div className="py-2">
                      {statusOptions.map((item) => (
                        <button
                          key={item.value}
                          className={cn(
                            'flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/40',
                            selectedStatusValue === item.value ? 'bg-accent/50' : ''
                          )}
                          onMouseDown={(e) => {
                            // 提前加锁，防止 onClick 之后才加锁导致已被关闭
                            e.stopPropagation();
                            hoverLockRef.current = Date.now() + LOCK_MS;
                            setHoverOpen(true);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedStatusValue(item.value);
                            // 选择后短暂锁定 HoverCard，避免立即关闭
                            hoverLockRef.current = Date.now() + LOCK_MS;
                            setStatusPopoverOpen(false);
                            setHoverOpen(true);
                          }}
                        >
                          <span className={cn('mt-1 h-2 w-2 rounded-full', statusAccentMap[item.value])} />
                          <span className="flex flex-col items-start gap-0.5 text-sm">
                            <span className="flex items-center gap-1 font-medium text-foreground">
                              {item.label}
                              {selectedStatusValue === item.value && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {item.description}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <TooltipWrapper text={t('common.save')}>
                  <Button
                    variant="outline"
                    size="icon"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // 阻止原生事件的进一步传播
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // 阻止原生事件的进一步传播
                      e.nativeEvent.stopImmediatePropagation();
                      handleSaveWord();
                    }}
                    disabled={
                      !userSettings?.myVocabularySetting ||
                      savingWord[word] ||
                      (!explanations[word] && !translations[word])
                    }
                    className={cn(
                      'h-8 w-8 rounded-lg border border-border bg-background/60 transition-colors',
                      isCardSaved(word)
                        ? 'border-primary bg-primary/10 hover:bg-primary/20'
                        : 'hover:border-primary/50 hover:bg-accent'
                    )}
                  >
                    {savingWord[word] ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    ) : isCardSaved(word) ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : (
                      <ClipboardCopy
                        className={cn(
                          "h-4 w-4 text-muted-foreground",
                          userSettings?.myVocabularySetting
                            ? "opacity-80"
                            : "opacity-40"
                        )}
                      />
                    )}
                  </Button>
                </TooltipWrapper>
                <TooltipWrapper text={t('common.speak')}>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-lg border border-border bg-background/60 hover:border-primary/50 hover:bg-accent"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      if (desktopSpeakerRef?.current) {
                        desktopSpeakerRef.current.click();
                      }
                    }}
                  >
                    <SpeakerIcon
                      divRef={desktopSpeakerRef}
                      size={8}
                      provider="EdgeTTS"
                      text={word}
                      lang={getSpeakerLang(userSettings?.languageSetting || "english") as any}
                      voice={undefined}
                      rate={10}
                      volume={100}
                    />
                  </Button>
                </TooltipWrapper>
              </div>
            )}
          </div>

          {/* Content Section */}
          {(loading[word] || youdaoLoadingState[word]) ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="h-4 w-2/4 rounded bg-muted" />
            </div>
          ) : (explanations[word] || translations[word]) && (
            <div>
              {/* 翻译显示 - 优先数据库，其次有道翻译（移动到内容区，使用整行宽度） */}
              <div className="flex items-start">
                <div className="w-[20px] flex items-center justify-center mt-0.5">
                  <MessageCircle className="h-4 w-4 flex-shrink-0 text-primary" />
                </div>
                <div className="pl-2 text-[12.5px] text-muted-foreground">
                  {explanations[word]?.translation ? (
                    typeof explanations[word]?.translation === 'string' ? (
                      explanations[word]?.translation?.split(';').map((part: string, index: number) => (
                        <div key={index} className={index > 0 ? "mt-1" : ""}>
                          {part.trim()}
                        </div>
                      ))
                    ) : (
                      <div>{explanations[word]?.translation}</div>
                    )
                  ) : (
                    translations[word]?.translation || translations[word]?.explains?.join('; ') || word
                  )}
                </div>
              </div>
              {lemmaInfo.value && (
                <div className="flex items-start gap-2 pl-2 mt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    {lemmaInfo.source === 'heuristic'
                      ? t('vocabulary.baseFormSuggestedLabel', 'Base form (suggested)')
                      : t('vocabulary.baseFormLabel', 'Base form')}
                    {": "}
                    <span className="font-semibold normal-case text-foreground">{lemmaInfo.value}</span>
                  </span>
                </div>
              )}
              {/* 显示例句 - 数据库中的例句 */}
              {explanations[word]?.example && (
                <div className="flex items-start space-x-2 pt-2 border-t border-border">
                  <Quote className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground italic">
                    {explanations[word]?.example}
                  </span>
                </div>
              )}

              {!explanations[word]?.example && translations[word]?.explains && translations[word]?.explains?.length > 1 && (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                <div className="flex items-start gap-3">
                  <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <Quote className="h-3.5 w-3.5" />
                  </span>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {translations[word]?.explains?.slice(1).map((explain: string, index: number) => (
                      <div key={index}>{explain}</div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
} 
